import { type DefaultObject } from '@moniebook-web/types';
import {
    getApiErrorMessage,
    SHARED_QUERY_KEY,
    type ErrorResponse,
} from '@moniebook-web/utils';
import { type AxiosError } from 'axios';
import { isTinyFloatingPointError } from 'src/transform';
import { Axios } from '../api';
import { invalidateQuery } from '../cache';
import { EVERY_MINUTE, REGISTERS_API_URL } from '../constants';
import { type AppDatabase } from '../db';
import { logger } from '../log';
import { type UnsyncedSale } from '../models';
import { QUERY_KEYS } from '../query-keys';
import { useBusinessStore } from '../store';
import {
    BOOLEAN_VALUE,
    SALE_LOG_EVENT,
    type MappedNewSaleResponse,
    type NewSale,
} from '../types';

const LOCK_TIMEOUT_MS = 2 * EVERY_MINUTE;
export const SALE_SYNC_BATCH_SIZE = 5;

export class UnsyncedSaleService {
    constructor(private readonly db: AppDatabase) {
        this.db = db;
    }

    async add(data: NewSale, metadata: { customerName?: string }) {
        const id = Date.now().toString();
        const {
            businessInfo: { branchId = '', registerIndex },
        } = useBusinessStore.getState() ?? {};

        const existing = await this.getByOrderNumber(data.orderNumber);

        if (existing) {
            logger.warn({
                eventName: SALE_LOG_EVENT,
                message: `[unsynced-sale] duplicate orderNumber detected on add => ${JSON.stringify(
                    {
                        orderNumber: data.orderNumber,
                        existingSaleId: existing.id,
                        branchId,
                        registerId: registerIndex,
                        staffId: data.staffId,
                    },
                )}`,
            });
        }

        await this.db.unsyncedSales.add({
            id,
            sale: data,
            orderDate: data.orderDate,
            failed: BOOLEAN_VALUE.FALSE,
            orderNumber: data.orderNumber,
            customerName: metadata.customerName ?? null,
            branchId,
            lockedAt: null,
        });

        return id;
    }

    async getCount() {
        const branchId =
            useBusinessStore.getState().businessInfo.branchId ?? '';

        return this.db.unsyncedSales
            .where('branchId')
            .anyOf([branchId, ''])
            .count();
    }

    async getUnsyncedSalesTableData(searchTerm?: string) {
        let sales: UnsyncedSale[];
        const branchId =
            useBusinessStore.getState().businessInfo.branchId ?? '';

        sales = await this.db.unsyncedSales
            .where('branchId')
            .anyOf([branchId, ''])
            .sortBy('orderDate');

        if (searchTerm) {
            const normalizedSearchTerm = searchTerm.toLowerCase();

            sales = sales.filter((sale) => {
                const orderNumber = sale.orderNumber?.toLowerCase() ?? '';
                return orderNumber.includes(normalizedSearchTerm);
            });
        }

        const allPaymentMethods = await this.db.paymentMethods
            .where('[enabled+deleted]')
            .equals([BOOLEAN_VALUE.TRUE, BOOLEAN_VALUE.FALSE])
            .toArray();

        const allStaff = await this.db.staff
            .where('id')
            .anyOf(sales.map((sale) => sale.sale.staffId ?? ''))
            .toArray();

        return { sales, allPaymentMethods, allStaff };
    }

    async getUnsyncedSaleDetailData(id: string) {
        const saleData = await this.db.unsyncedSales.get(id);

        if (!saleData) {
            return null;
        }

        const sale = saleData?.sale;

        const staffId = sale.staffId;
        const paymentsIds = sale?.payments?.map(
            (payment) => payment.paymentMethodId,
        );

        const [staff, payments] = await Promise.all([
            this.db.staff
                .where('[id+deleted]')
                .equals([staffId ?? '', BOOLEAN_VALUE.FALSE])
                .first(),
            this.db.paymentMethods.where('id').anyOf(paymentsIds).toArray(),
        ]);

        return {
            saleData,
            payments,
            staff,
        };
    }

    async getPendingSalesForSync() {
        const now = Date.now();
        const branchId =
            useBusinessStore.getState().businessInfo.branchId ?? '';

        const isEligible = (sale: UnsyncedSale) => {
            if (sale.lockedAt === null) {
                return true;
            }
            return now - sale.lockedAt >= LOCK_TIMEOUT_MS;
        };

        return this.db.unsyncedSales
            .where('[branchId+failed]')
            .anyOf([
                [branchId, BOOLEAN_VALUE.FALSE],
                ['', BOOLEAN_VALUE.FALSE],
            ])
            .filter(isEligible)
            .toArray();
    }

    getById(id: string) {
        return this.db.unsyncedSales.get(id);
    }

    async getUsedTransactionReferences(): Promise<Set<string>> {
        const branchId =
            useBusinessStore.getState().businessInfo.branchId ?? '';

        const sales = await this.db.unsyncedSales
            .where('branchId')
            .anyOf([branchId, ''])
            .toArray();

        const references = sales.flatMap((sale) =>
            sale.sale.payments.map(
                (payment) =>
                    payment.merchantReference ?? payment.paymentDetails?.ref,
            ),
        );

        return new Set(references.filter((ref): ref is string => Boolean(ref)));
    }

    getByOrderNumber(orderNumber: string) {
        return this.db.unsyncedSales
            .where('orderNumber')
            .equals(orderNumber)
            .first();
    }

    async syncPendingSales(batchSize = SALE_SYNC_BATCH_SIZE) {
        const salesToSync = await this.getPendingSalesForSync();

        const salesWithoutNaNValues = salesToSync.map((sale) => ({
            ...sale,
            sale: {
                ...sale.sale,
                items: sale.sale.items.filter(
                    (item) =>
                        !isTinyFloatingPointError(item.quantity) &&
                        !isTinyFloatingPointError(item.subTotal) &&
                        !isTinyFloatingPointError(item.total),
                ),
            },
        }));

        let hasFailure = false;

        for (
            let index = 0;
            index < salesWithoutNaNValues.length;
            index += batchSize
        ) {
            const batch = salesWithoutNaNValues.slice(index, index + batchSize);

            const results = await Promise.all(
                batch.map(async (sale) => this.recordSaleById(sale.id)),
            );

            if (results.some((result) => !result.success)) {
                hasFailure = true;
            }
        }

        return { hasFailure };
    }

    delete(id: string) {
        return this.db.unsyncedSales.delete(id);
    }

    async recordSaleById(selectedSaleId: string) {
        const lockAcquired = await this.acquireLock(selectedSaleId);

        if (!lockAcquired) {
            return { success: false, error: 'Sale is currently being synced' };
        }

        const sale = await this.db.unsyncedSales.get(selectedSaleId);

        if (!sale?.id) {
            return { success: false, error: 'Sale not found' };
        }

        try {
            const paramsSale = sale.sale;
            paramsSale.payments.forEach((element) => {
                if (
                    element.paymentDetails &&
                    typeof element.paymentDetails['meta'] !== 'string'
                ) {
                    element.paymentDetails['meta'] = '';
                }
            });

            paramsSale.items = paramsSale.items
                .map((item) => ({
                    ...item,
                    quantity: Number.parseFloat(item.quantity.toFixed(9)),
                }))
                .filter(
                    (item) =>
                        !isTinyFloatingPointError(item.quantity) &&
                        !isTinyFloatingPointError(item.subTotal) &&
                        !isTinyFloatingPointError(item.total),
                );

            await Axios.post<MappedNewSaleResponse>(
                REGISTERS_API_URL.SALE.RECORD,
                paramsSale, // sale.sale,
            );

            await this.db.unsyncedSales.delete(selectedSaleId);

            await invalidateQuery([
                SHARED_QUERY_KEY.GET_PAGINATED_SALE,
                QUERY_KEYS.GET_SALE,
                QUERY_KEYS.GET_OUTSTANDING_SALES,
                QUERY_KEYS.CUSTOMERS.ALL,
            ]);
            return { success: true, error: null };
        } catch (error) {
            const axiosError = error as AxiosError;

            if (axiosError.response) {
                await this.db.unsyncedSales.update(sale.id, {
                    failed: BOOLEAN_VALUE.TRUE,
                    lockedAt: null,
                });
            } else {
                await this.releaseLock(sale.id);
            }

            const errorMessage = getApiErrorMessage(
                axiosError as unknown as Partial<ErrorResponse<DefaultObject>>,
            );

            return {
                success: false,
                error: errorMessage ?? 'Unknown error',
            };
        }
    }

    private async acquireLock(saleId: string): Promise<boolean> {
        let acquired = false;

        await this.db.unsyncedSales
            .where('id')
            .equals(saleId)
            .modify((sale) => {
                const now = Date.now();
                const isLocked =
                    sale.lockedAt !== null &&
                    now - sale.lockedAt < LOCK_TIMEOUT_MS;

                if (isLocked) {
                    return;
                }

                sale.lockedAt = now;
                acquired = true;
            });

        return acquired;
    }

    private async releaseLock(saleId: string): Promise<void> {
        await this.db.unsyncedSales.update(saleId, { lockedAt: null });
    }
}
